import { compose, test, splitEvery, join, cond, T, curry, lt, ifElse } from 'ramda';
import { isEmail, isCreditCard, isPostalCode, isCurrency } from 'validator';
import { isAfter } from 'date-fns';
import { stubNull } from 'ramda-adjunct';
import { FormArray, FormGroup } from '@angular/forms';

import { get } from './form.helpers';
import { nilArgs } from './null.helpers';
import { toFloat } from './number.helpers';
import { valueCode } from './code.helpers';
import { unequal } from './function.helpers';
import { isValidTopLevel } from './top-level.helpers';
import { useNumericValues, useValues, value } from './value.helpers';
import { validatorError, hoursbyPeriod, setValidationError, compareControls } from './validation.helpers';
import { before30Days, before120Years, before60Days, after10Months, beforeSecondMonth, withInNineMonths, vehicleYearBetween, invalidFutureDays, before5Months, before1Year, invalidTodayDate, before90Days } from './date.helpers';

import { ErrorCodes } from '@app/models/errors.model';

const isStartAfterEnd = ifElse(nilArgs, stubNull, isAfter);

// {6,100}           - Assert password is between 6 and 100 characters
// (?=.*[0-9])       - Assert a string has at least one number
const isPassword = test(/^(?=.*[0-9])[a-zA-Z0-9!@#$%^&*]{6,100}$/);
const isValidName = test(/^(?!\s)[a-zA-Z0-9\s-./'’]{1,100}$/);
const isValidOrgName = test(/^(?!\s)[a-zA-Z0-9\s-'’]{1,100}$/);
const isValidAssistingOrgName = test(/^(?!\s)[a-zA-Z0-9\s-'']+$/);
const isValidBusinessName = test(/^(?!\s)[a-zA-Z0-9\s-'']+$/);
const isValidCompanyName = test(/^[a-zA-Z0-9\s-./'’]{1,100}$/);
const isValidInsuranceCompanyName = test(/^(?!\s)[a-zA-Z0-9\s-,./'’]{1,100}$/);
const isValidSignatureName = test(/^(?!\s)[a-zA-Z0-9\s-.'’]{1,100}$/);
const initialSpace = test(/^(?!\s)/);
const isHouseNumber = compose(
  test(/^[a-zA-Z0-9\s/]*$/),
  String
);

const isExpense = compose(
  isCurrency,
  toFloat
);

const isUnit = compose(
  test(/^[a-zA-Z0-9 \/]*$/),
  String
);

// Number Validator
const isNumber = compose(
  test(/^-?(0|[1-9]\d*)?$/),
  String
);

// EmployerName Validator
const isValidEmployerName = compose(
  test(/^(?!\s)[a-zA-Z0-9\s-' .,/&]*$/),
  String
);



const isStreetAddress = compose(
  test(/^(?!\s)[a-zA-Z0-9\s-' ,/#]*$/),
  String
);

// All characters except: ^, #, %, *, { }, [ ], < >, |
const isComments = compose(
  test(/^[^#%*{}<>|[\]^]*$/),
  String
);

const isSubmittedBy = compose(
  test(/^[^~#%*{}<>|[\]^]*$/),
  String
);

const isZipCode = compose(
  zip => isPostalCode(zip, 'US'),
  join('-'),
  splitEvery(5)
);

// 5-digit zip code validator for RIDP
const isZipCode5Digit = compose(
  test(/^\d{5}$/),
  String
);

// 4-digit zip extension validator for RIDP (optional)
const isZipExtension4Digit = compose(
  test(/^\d{4}$/),
  String
);

const isCity = compose(
  test(/^(?!\s)[a-zA-Z\s]*$/),
  String
);

const isLanguage = compose(
  test(/^(?!\s)[a-zA-Z\s]*$/),
  String
);

const isTribeName = compose(
  test(/^[a-zA-Z0-9\s-']*$/),
  String
);

const isMedicareNumber = compose(
  test(/^[a-zA-Z0-9{]*$/),
  String
);

// const isInvalidField = compose(
//   test(/^[a-zA-Z0-9-' .,/&@"]*$/),
//   String
// );

const isValidDate = compose(
  test(/^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/),
  String
);

const isEmailAddress = compose(
  isEmail,
  String
);

const isValidPhNumber = compose(
  test(/[1-9]{1}[0-9]{2}[1-9]{1}[0-9]{2}[0-9]{4}/),
  String
);

const isValidPhNumberHOH = compose(
  test(/[1-9]{1}[0-9]{2}[0-9]{1}[0-9]{2}[0-9]{4}/),
  String
);
const isAlien = test(/^\d{7,9}$/);
const isSevis = test(/^\d{10}/);
const isCard = test(/^[a-zA-Z]{3}\d{10}/)
const isI94 = test(/^[0-9]{9}[a-zA-Z0-9]{1}[0-9]{1}$/);
const isDocDecription = test(/^[a-zA-Z0-9.' \/-]*$/);

const iscitizenshipNumber = test(/^[a-zA-Z0-9]{6,12}$/);

const isAlphaNumeric = test(/^[a-z0-9]+$/i);
const isAlphaNumericAppNumber = test(/^[a-zA-Z]{1}\d{8}/);
const isAlphaNumericHyphen = test(/^[a-z0-9-]+$/i);
const orgName = test(/^[a-z0-9 ]+$/i);
const currency = test(/^[0-9]\d*(\.\d+)?$/);
// const isPassport = test(/^([a-zA-Z0-9]){20,}$/);
const isPassport = test(/^[a-zA-Z0-9]{1,20}$/);

const numeric = test(/^[0-9]*(\.[0-9]{0,2})?$/)
const isSplCharacter = test(/^[A-Za-z0-9]+$/);
const isUserNameEmail = test(/^[A-Za-z0-9]+$/);
const isWithinLength = test(/^.{6,20}$/);
const noSpace = test(/^\s-/);
const isWithinAnswerLength = test(/^.{3,}$/)

const noDecimal = test(/^[0-9]*$/);
const percentage = (value) => value <= 100;
const isValidFirstLastName = test(/^[^-\s]([A-Za-z- '])*$/);
const isValidMedAplCallerFirstLastName = test(/^[A-Za-z][A-Za-z '-]*$/);
const isValidTextarea = test(/^[A-Za-z0-9\s#\-\/.,&']*$/);
const isValidSSN = test(/^\d{3}(?!00)\d{2}\d{4}$/);
const isPersonId = test(/^[0-9]{9,10}$/);
const firstNameRidp = test(/^((([A-Za-z])([A-Za-z \-'])*([A-Za-z']))|([A-Za-z]))$/)
const middleNameRidp = test(/^[A-Za-z]+( [A-Za-z]+)*$/)
const lastNameRidp = test(/^([A-Za-z]|[DLOdlo]')([A-Za-z \\-]|[DLOdlo]')*([A-Za-z]|[DLOdlo]')$/)
//const middleNameRidp = test(/^([a-zA-Z]{1,32})$/)
const dobRidp = test(/((((19|2[0-9])([2468][048]|[13579][26]|0[48])|2[048]00)-02-29|((19|2[0-9])[0-9]{2}-(0[469]|11)-(0[1-9]|[12][0-9]|30)|(19|2[0-9])[0-9]{2}-(0[13578]|1[02])-(0[1-9]|[12][0-9]|3[01])|(19|2[0-9])[0-9]{2}-02-(0[1-9]|1[0-9]|2[0-8]))))/)
const streetNameRidp = test(/^([a-zA-Z0-9/.\# \-\\]{1,60})$/)
const cityNameRidp = test(/^[A-Za-z'.\\\- ]{1,38}$/)
const emailidRidp = test(/^[\w\-.]+@[\w\-.]+$/)
const isOtherLanguage = test(/^(?! )[A-Za-z '\-]+$/)

export const fnValidator = validatorError(firstNameRidp, ErrorCodes.INVALID_FIRST);
export const lnValidator = validatorError(lastNameRidp, ErrorCodes.INVALID_LAST);
export const mnValidator = validatorError(middleNameRidp, ErrorCodes.INVALID_MIDDLE_RIDP);
export const dobbValidator = validatorError(dobRidp, ErrorCodes.INVALID_DATE);
export const strtnameValidator = validatorError(streetNameRidp, ErrorCodes.INVALID_STREET_ADDRESS);
export const citynameValidator = validatorError(cityNameRidp, ErrorCodes.INVALID_CITY);
export const emailidValidator = validatorError(emailidRidp, ErrorCodes.INVALID_EMAIL_ADDRESS);
export const creditCardValidator = validatorError(isCreditCard, ErrorCodes.INVALID_CREDITCARD);
export const emailValidator = validatorError(isEmailAddress, ErrorCodes.INVALID_EMAIL_ADDRESS);
export const passwordValidator = validatorError(isPassword, ErrorCodes.INVALID_PASSWORD);
export const expenseValidator = validatorError(isExpense, ErrorCodes.INVALID_EXPENSE);
export const numericValidator = validatorError(numeric, ErrorCodes.INVALID_NUMERIC);
export const percentageValidator = validatorError(percentage, ErrorCodes.INVALID_PRECENTAGE);
export const individualNameValidator = validatorError(isValidName, ErrorCodes.INVALID_NAME);
export const individualOrgNameValidator = validatorError(isValidOrgName, ErrorCodes.INVALID_ORG_NAME);
export const assistingOrgNameValidator = validatorError(isValidAssistingOrgName, ErrorCodes.INVALID_ASSISTING_ORG_NAME);
export const idNumberValidator = validatorError(isValidAssistingOrgName, ErrorCodes.INVALID_ASSISTING_ID_NUMBER);
export const employerNameValidator = validatorError(isValidEmployerName, ErrorCodes.INVALID_EMPLOYER_NAME);
export const noInitialSpaceValidator = validatorError(initialSpace, ErrorCodes.required)
export const firstNameValidator = validatorError(isValidFirstLastName, ErrorCodes.INVALID_FIRST);
export const lastNameValidator = validatorError(isValidFirstLastName, ErrorCodes.INVALID_LAST);
export const medAplCallerFirstNameValidator = validatorError(isValidMedAplCallerFirstLastName, ErrorCodes.INVALID_FIRST);
export const medAplCallerLastNameValidator = validatorError(isValidMedAplCallerFirstLastName, ErrorCodes.INVALID_LAST);
export const textareaValidator = validatorError(isValidTextarea, ErrorCodes.INVALID_TEXTAREA);
export const otherExpenseValidator = validatorError(isValidName, ErrorCodes.INVALID_OTHER_EXPENSE);
export const nameValidator = validatorError(isValidName, ErrorCodes.INVALID_TAX_NAME);
export const companyNameValidator = validatorError(isValidCompanyName, ErrorCodes.INVALID_TAX_NAME);
export const insuranceCompanyNameValidator = validatorError(isValidInsuranceCompanyName, ErrorCodes.INVALID_INSURANCE_COMPANY_NAME);
export const nursingNameValidator = validatorError(isValidName, ErrorCodes.INVALID_NURSING_NAME);
export const descriptionValidator = validatorError(isValidName, ErrorCodes.INVALID_DESCRIPTION);
export const policyNumberValidator = validatorError(isAlphaNumeric, ErrorCodes.INVALID_ALPHANUMERIC_CHARACTER);
export const businessNameValidator = validatorError(isValidBusinessName, ErrorCodes.INVALID_BUSINESS_NAME);
export const safeAtHomeMailValidator = validatorError(isAlphaNumericHyphen, ErrorCodes.INVALID_SAH_ALPHANUMERIC_CHARACTER);
export const organizationNameValidator = validatorError(orgName, ErrorCodes.INVALID_ORGANIZATION_NAME);
export const houseNumberValidator = validatorError(isHouseNumber, ErrorCodes.INVALID_HOUSE_NUMBER);
export const unitValidator = validatorError(isUnit, ErrorCodes.INVALID_UNIT_NUMBER);
export const numberValidator = validatorError(isNumber, ErrorCodes.INVALID_NUMBER);
export const decimalValidator = validatorError(noDecimal, ErrorCodes.INVALID_PERCENTAGE_DECIMAL);
export const streetAddressValidator = validatorError(isStreetAddress, ErrorCodes.INVALID_STREET_ADDRESS);
export const commentsValidator = validatorError(isComments, ErrorCodes.INVALID_COMMENTS);
export const zipCodeValidator = validatorError(isZipCode, ErrorCodes.INVALID_ZIP_CODE);
export const zipCode5DigitValidator = validatorError(isZipCode5Digit, ErrorCodes.INVALID_ZIP_CODE);
export const zipExtension4DigitValidator = validatorError(isZipExtension4Digit, ErrorCodes.INVALID_ZIP_CODE);
export const cityValidator = validatorError(isCity, ErrorCodes.INVALID_CITY);
export const languageValidator = validatorError(isLanguage, ErrorCodes.INVALID_LANGUAGE);
export const tribeNameValidator = validatorError(isTribeName, ErrorCodes.INVALID_TRIBE_NAME);
export const medicareNumberValidator = validatorError(isMedicareNumber, ErrorCodes.INVALID_MEDICARE_NUMBER);
export const topLevelValidator = validatorError(isValidTopLevel, ErrorCodes.INVALID_TOP_LEVEL);
export const dateNotAfterMonthValidator = validatorError(beforeSecondMonth, ErrorCodes.DATE_NOT_AFTER_MONTH);
export const dueDateValidator = validatorError(withInNineMonths, ErrorCodes.DATE_DUE);
export const maxDueDateValidator = validatorError(after10Months, ErrorCodes.MAX_DATE_DUE);
export const returnDateValidator = validatorError(invalidTodayDate, ErrorCodes.INVALID_MAX_DATE);
export const dateNotBefore30DaysValidator = validatorError(before30Days, ErrorCodes.DATE_NOT_BEFORE_30_DAYS);
export const dateNotBefore90DaysValidator = validatorError(before90Days, ErrorCodes.DATE_NOT_BEFORE_90_DAYS);
export const dateNotBefore120YearsValidator = validatorError(before120Years, ErrorCodes.DATE_NOT_BEFORE_120_YEARS);
export const babyDateNotGreaterThan1Year = validatorError(before1Year, ErrorCodes.BABY_DATE_NOT_GREATER_THAN_1_YEAR);
export const dateNotBefore60DaysValidator = validatorError(before60Days, ErrorCodes.DATE_NOT_BEFORE_60_DAYS);
export const dateNotBefore5MonthsValidator = validatorError(before5Months, ErrorCodes.FIVE_MONTHS_MAX);
export const dateNotBefore12MonthsValidator = validatorError(before5Months, ErrorCodes.TWELVE_MONTHS_MAX);
export const invalidFutureDate = validatorError(invalidFutureDays, ErrorCodes.INVALID_FUTURE_DATE);
export const unmatchedFieldsValidator = compareControls(unequal, ErrorCodes.UNMATCHED_FIELDS, useValues);
export const dateStartAfterEndValidator = compareControls(isStartAfterEnd, ErrorCodes.DATE_START_AFTER_END, useValues);
export const invalidFieldValidator = validatorError(isValidName, ErrorCodes.INVALID_FIELD);
export const validDateValidator = validatorError(isValidDate, ErrorCodes.INVALID_DATE);
export const vehicleYearValidator = validatorError(vehicleYearBetween, ErrorCodes.INVALID_VEHICLE_YEAR);
export const lessThanValidator = errorCode => compareControls(lt, errorCode, useNumericValues);
export const documentDescriptionValidator = validatorError(isDocDecription, ErrorCodes.INVALID_DOCUMENT_DESCRIPTION);
export const phoneNumberValidator = validatorError(isValidPhNumber, ErrorCodes.PHONE_NUMBER);
export const phoneNumberValidatorHOH = validatorError(isValidPhNumberHOH, ErrorCodes.PHONE_NUMBER);
export const i94Validator =  validatorError(isI94, ErrorCodes.INVALID_I94);
export const alphanumericValidator = validatorError(isAlphaNumericAppNumber, ErrorCodes.INVALID_ALPHANUMERIC_CHARACTER);
export const alienValidator = validatorError(isAlien, ErrorCodes.INVALID_ALIEN_NUMBER);
export const cardNumberValidator = validatorError(isCard, ErrorCodes.INVALID_CARD_NUMBER);
export const sevisValidator = validatorError(isSevis, ErrorCodes.INVALID_SEVIS_ID);
export const certificateValidator = validatorError(iscitizenshipNumber, ErrorCodes.INVALID_CITIZENSHIP_CERTIFICATE);
export const naturalizationValidator = validatorError(iscitizenshipNumber, ErrorCodes.INVALID_NATURALIZATION_NUMBER);
export const currencyValidator = validatorError(currency, ErrorCodes.INVALID_CURRENCY);
export const hoursValidator = validatorError(currency, ErrorCodes.INVALID_HOURS);
export const ssnValidator = validatorError(isValidSSN, ErrorCodes.INVALID_SSN);
export const personIdValidator = validatorError(isPersonId, ErrorCodes.INVALID_PERSON_ID);
export const passportValidator = validatorError(isPassport, ErrorCodes.INVALID_PASSPORT_NUMBER);
export const splCharValidator = validatorError(isSplCharacter, ErrorCodes.INVALID_USER_NAME);
export const userNameEmail = validatorError(isUserNameEmail, ErrorCodes.INVALID_USER_NAME_EMAIL);
export const userNameLength = validatorError(isWithinLength, ErrorCodes.INVALID_LENGTH);
export const noSpaceInUsername = validatorError(noSpace, ErrorCodes.NO_SPACE);
export const answerValidator = validatorError(isWithinAnswerLength, ErrorCodes.INVALID_ANSWER_LENGTH);
export const answerLengthValidator = validatorError(isValidEmployerName, ErrorCodes.INVALID_ANSWER);
export const submittedByValidator = validatorError(isSubmittedBy, ErrorCodes.INVALID_SUBMITTEDBY);
export const signatureNameValidator = validatorError(isValidSignatureName, ErrorCodes.INVALID_TAX_NAME);
export const aplSubmittedByValidator = validatorError(isSubmittedBy, ErrorCodes.INVALID_APL_SUBMITTEDBY);
export const aplRelationshipValidator = validatorError(isSubmittedBy, ErrorCodes.INVALID_APL_RELATIONSHIP);
export const aplResponseValidator = validatorError(isSubmittedBy, ErrorCodes.INVALID_APL_RESPONSE);
export const aplPetitionResponseValidator = validatorError(isSubmittedBy, ErrorCodes.INVALID_APL_PITN_RESPONSE);
export const otherLanguageValidator = validatorError(isOtherLanguage, ErrorCodes.INVALID_OTHERLANGUAGE);

const validatePeriodHours = control =>
  cond([
    [hoursbyPeriod('B', 173), () => setValidationError(control, ErrorCodes.PERIOD_BIWEEKLY)],
    [hoursbyPeriod('M', 346), () => setValidationError(control, ErrorCodes.PERIOD_MONTHLY)],
    [hoursbyPeriod('S', 160), () => setValidationError(control, ErrorCodes.PERIOD_SEMIMONTHLY)],
    [hoursbyPeriod('W', 80), () => setValidationError(control, ErrorCodes.PERIOD_WEEKLY)],
    [T, stubNull],
  ]);

export const wagesPeriodHourValidator = curry((wagesPeriod, hoursAverage, control) => {
  const periodControl = get(wagesPeriod, control);
  const hoursControl = get(hoursAverage, control);

  return validatePeriodHours(hoursControl)({
    period: valueCode(periodControl),
    hours: value(hoursControl),
  });
});

export const totalValidator = (control : FormGroup) => {
  const jointOwnershipInformation = control.controls['jointOwnershipInformation'] as FormArray;
  const total = jointOwnershipInformation.controls.reduce((total, current: FormGroup) => {
    const value = (+current.controls['jointOwnerPercentage'].value);
    return value + total;
  }, 0);
  return total === 100 || jointOwnershipInformation.disabled ? null : {error : 'Please make sure that the percentages of ownership add up to 100%'};
}

export const dropdownValidator =  (control : FormGroup) => {
  const jointOwnershipInformation = control.controls['jointOwnershipInformation'] as FormArray;
  const values = jointOwnershipInformation.value.filter(item => item.jointOwnerIndividual?.code !== 'EMPTY').map(item => item.jointOwnerIndividual);
  const valueArr = values.map(val =>  {if (val) return val.code });
  const isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) != idx );
  return  !isDuplicate || jointOwnershipInformation.disabled ? null : {error : 'dropdown not correct'};
}
